// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module createIdentify
 * @package @geenius/analytics/solidjs
 * @description Wraps identity-management actions for the SolidJS variant so
 * consumers can identify or reset a user without re-plumbing tracker access.
 */

import type { UserTraits } from "@geenius/analytics";
import { createAnalytics } from "./createAnalytics";

/**
 * Identity-management helpers returned by {@link createIdentify}.
 *
 * @property identify Associates the current session with a user and traits.
 * @property reset Clears the current analytics identity and session state.
 */
export interface CreateIdentifyReturn {
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  reset: () => Promise<void>;
}

/**
 * Returns identify and reset helpers backed by the active analytics tracker.
 *
 * @returns Identity-management functions for the active tracker.
 */
export function createIdentify(): CreateIdentifyReturn {
  const analytics = createAnalytics();

  return {
    identify: (id: string, traits?: UserTraits) =>
      analytics.identify(id, traits),
    reset: () => analytics.reset(),
  };
}
